import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import copy from 'copy-to-clipboard';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Add, ContentCopy, Delete, Refresh, Visibility, VisibilityOff} from '@mui/icons-material';
import {useApplicationContext} from 'ApplicationContext';
import {OidcClientCreatedDto, OidcClientDto} from 'apiClient/Api';

type ClientType = 'confidential' | 'public';

const DEFAULT_SCOPES = ['support_api', 'openid', 'profile', 'email', 'offline_access'];

const isValidUri = (value: string): boolean => {
    if (!value) return false;
    try {
        const u = new URL(value);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
};

const OidcClients: React.FC = () => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [clients, setClients] = useState<OidcClientDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createClientId, setCreateClientId] = useState('');
    const [createDisplayName, setCreateDisplayName] = useState('');
    const [createClientType, setCreateClientType] = useState<ClientType>('confidential');
    const [createRedirectUris, setCreateRedirectUris] = useState<string[]>([]);
    const [createPostLogoutUris, setCreatePostLogoutUris] = useState<string[]>([]);
    const [createScopes, setCreateScopes] = useState<string[]>(['support_api']);
    const [createAllowClientCredentials, setCreateAllowClientCredentials] = useState(false);
    const [createIsTrusted, setCreateIsTrusted] = useState(false);
    const [creating, setCreating] = useState(false);

    // Track each Autocomplete's pending input so we can commit it on blur / submit
    // — otherwise a typed-but-unconfirmed URI silently disappears.
    const [redirectUriInput, setRedirectUriInput] = useState('');
    const [postLogoutUriInput, setPostLogoutUriInput] = useState('');
    const [scopeInput, setScopeInput] = useState('');

    const mergePending = (list: string[], pending: string): string[] => {
        const trimmed = pending.trim();
        if (!trimmed || list.includes(trimmed)) return list;
        return [...list, trimmed];
    };

    const [revealed, setRevealed] = useState<OidcClientCreatedDto | null>(null);
    const [secretVisible, setSecretVisible] = useState(false);

    const fetchClients = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await context.api.oidcClients.oidcClientsList();
            setClients(response.data ?? []);
        } catch {
            setError(t('oidcClients.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.oidcClients, t]);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    const openCreateDialog = () => {
        setCreateClientId('');
        setCreateDisplayName('');
        setCreateClientType('confidential');
        setCreateRedirectUris([]);
        setCreatePostLogoutUris([]);
        setCreateScopes(['support_api']);
        setCreateAllowClientCredentials(false);
        setCreateIsTrusted(false);
        setRedirectUriInput('');
        setPostLogoutUriInput('');
        setScopeInput('');
        setCreateOpen(true);
    };

    // Effective lists fold the currently-typed (uncommitted) input into the array
    // so the user sees the validation hint immediately and the submit handler
    // can trust these as the source of truth.
    const effectiveRedirectUris = useMemo(
        () => mergePending(createRedirectUris, redirectUriInput),
        [createRedirectUris, redirectUriInput],
    );
    const effectivePostLogoutUris = useMemo(
        () => mergePending(createPostLogoutUris, postLogoutUriInput),
        [createPostLogoutUris, postLogoutUriInput],
    );
    const effectiveScopes = useMemo(
        () => mergePending(createScopes, scopeInput),
        [createScopes, scopeInput],
    );

    const redirectUrisInvalid = useMemo(
        () => effectiveRedirectUris.some((u) => !isValidUri(u)),
        [effectiveRedirectUris],
    );
    const postLogoutUrisInvalid = useMemo(
        () => effectivePostLogoutUris.some((u) => !isValidUri(u)),
        [effectivePostLogoutUris],
    );

    const canSubmit =
        createClientId.trim().length > 0 &&
        !redirectUrisInvalid &&
        !postLogoutUrisInvalid;

    const submitCreate = async () => {
        if (!canSubmit) return;
        setCreating(true);
        try {
            const response = await context.api.oidcClients.oidcClientsCreate({
                clientId: createClientId.trim(),
                displayName: createDisplayName.trim() || null,
                clientType: createClientType,
                redirectUris: effectiveRedirectUris.length ? effectiveRedirectUris : null,
                postLogoutRedirectUris: effectivePostLogoutUris.length ? effectivePostLogoutUris : null,
                scopes: effectiveScopes.length ? effectiveScopes : null,
                allowClientCredentials: createAllowClientCredentials,
                isTrusted: createIsTrusted,
            });
            setCreateOpen(false);
            setRevealed(response.data);
            setSecretVisible(false);
            await fetchClients();
        } catch (e: any) {
            const msg = e?.response?.data?.detail ?? e?.response?.data?.title ?? e?.message ?? t('oidcClients.create.error');
            enqueueSnackbar(msg, {variant: 'error'});
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (client: OidcClientDto) => {
        if (!client.clientId) return;
        if (!window.confirm(t('oidcClients.delete.confirm', {name: client.displayName || client.clientId}))) return;
        try {
            await context.api.oidcClients.oidcClientsDelete(client.clientId);
            enqueueSnackbar(t('oidcClients.delete.success'), {variant: 'success'});
            await fetchClients();
        } catch {
            enqueueSnackbar(t('oidcClients.delete.error'), {variant: 'error'});
        }
    };

    const copyToClipboard = (value: string) => {
        if (copy(value)) {
            enqueueSnackbar(t('oidcClients.copied'), {variant: 'success'});
        }
    };

    return (
        <Box>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Box>
                    <Typography variant="h5" component="h2">{t('oidcClients.title')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('oidcClients.subtitle')}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title={t('oidcClients.refresh')}>
                        <span>
                            <IconButton onClick={fetchClients} disabled={loading}>
                                {loading ? <CircularProgress size={20}/> : <Refresh/>}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<Add/>} onClick={openCreateDialog}>
                        {t('oidcClients.create.button')}
                    </Button>
                </Stack>
            </Stack>

            <Alert severity="info" sx={{mb: 2}}>{t('oidcClients.warningOnce')}</Alert>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            <Card variant="outlined">
                <CardContent sx={{p: 0, '&:last-child': {pb: 0}}}>
                    {loading && clients.length === 0 ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : clients.length === 0 ? (
                        <Box sx={{textAlign: 'center', py: 6}}>
                            <Typography color="text.secondary">{t('oidcClients.empty')}</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('oidcClients.columns.displayName')}</TableCell>
                                        <TableCell>{t('oidcClients.columns.clientId')}</TableCell>
                                        <TableCell>{t('oidcClients.columns.clientType')}</TableCell>
                                        <TableCell>{t('oidcClients.columns.consentType')}</TableCell>
                                        <TableCell>{t('oidcClients.columns.scopes')}</TableCell>
                                        <TableCell>{t('oidcClients.columns.redirectUris')}</TableCell>
                                        <TableCell align="right">{t('oidcClients.columns.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clients.map((c) => (
                                        <TableRow key={c.clientId} hover>
                                            <TableCell sx={{fontWeight: 500}}>{c.displayName || '-'}</TableCell>
                                            <TableCell sx={{fontFamily: 'monospace', fontSize: '0.85rem'}}>
                                                <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                                                    <span>{c.clientId}</span>
                                                    {c.clientId && (
                                                        <Tooltip title={t('oidcClients.copyClientId')}>
                                                            <IconButton size="small" onClick={() => copyToClipboard(c.clientId!)}>
                                                                <ContentCopy fontSize="inherit"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={c.clientType === 'public' ? 'default' : 'primary'}
                                                    label={t(`oidcClients.clientType.${c.clientType ?? 'confidential'}`)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={t(`oidcClients.consentType.${c.consentType ?? 'explicit'}`)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} sx={{flexWrap: 'wrap', gap: 0.5}}>
                                                    {(c.scopes ?? []).map((s) => (
                                                        <Chip key={s} size="small" label={s} sx={{fontFamily: 'monospace'}}/>
                                                    ))}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{maxWidth: 280}}>
                                                {(c.redirectUris ?? []).length === 0 ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('oidcClients.none')}
                                                    </Typography>
                                                ) : (
                                                    <Stack spacing={0.25}>
                                                        {c.redirectUris!.map((u) => (
                                                            <Typography
                                                                key={u}
                                                                variant="caption"
                                                                sx={{
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.78rem',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {u}
                                                            </Typography>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={t('oidcClients.delete.button')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDelete(c)}
                                                    >
                                                        <Delete fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{t('oidcClients.create.title')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{mt: 1}}>
                        <DialogContentText>{t('oidcClients.create.description')}</DialogContentText>

                        <TextField
                            autoFocus
                            label={t('oidcClients.create.clientIdLabel')}
                            value={createClientId}
                            onChange={(e) => setCreateClientId(e.target.value.replace(/\s+/g, ''))}
                            placeholder={t('oidcClients.create.clientIdPlaceholder')}
                            helperText={t('oidcClients.create.clientIdHelper')}
                            fullWidth
                            required
                            disabled={creating}
                            slotProps={{input: {sx: {fontFamily: 'monospace'}}}}
                        />

                        <TextField
                            label={t('oidcClients.create.displayNameLabel')}
                            value={createDisplayName}
                            onChange={(e) => setCreateDisplayName(e.target.value)}
                            placeholder={t('oidcClients.create.displayNamePlaceholder')}
                            helperText={t('oidcClients.create.displayNameHelper')}
                            fullWidth
                            disabled={creating}
                        />

                        <TextField
                            select
                            label={t('oidcClients.create.clientTypeLabel')}
                            value={createClientType}
                            onChange={(e) => setCreateClientType(e.target.value as ClientType)}
                            helperText={
                                createClientType === 'public'
                                    ? t('oidcClients.create.clientTypePublicHelper')
                                    : t('oidcClients.create.clientTypeConfidentialHelper')
                            }
                            fullWidth
                            disabled={creating}
                        >
                            <MenuItem value="confidential">{t('oidcClients.clientType.confidential')}</MenuItem>
                            <MenuItem value="public">{t('oidcClients.clientType.public')}</MenuItem>
                        </TextField>

                        <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={createRedirectUris}
                            onChange={(_, value) => setCreateRedirectUris(value as string[])}
                            inputValue={redirectUriInput}
                            onInputChange={(_, value, reason) => {
                                if (reason !== 'reset') setRedirectUriInput(value);
                            }}
                            onBlur={() => {
                                const merged = mergePending(createRedirectUris, redirectUriInput);
                                if (merged !== createRedirectUris) {
                                    setCreateRedirectUris(merged);
                                    setRedirectUriInput('');
                                }
                            }}
                            disabled={creating}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('oidcClients.create.redirectUrisLabel')}
                                    placeholder="https://app.example.com/callback"
                                    helperText={
                                        redirectUrisInvalid
                                            ? t('oidcClients.create.redirectUrisInvalid')
                                            : t('oidcClients.create.redirectUrisHelper')
                                    }
                                    error={redirectUrisInvalid}
                                />
                            )}
                            renderValue={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const valid = isValidUri(option);
                                    return (
                                        <Chip
                                            {...getTagProps({index})}
                                            key={option}
                                            label={option}
                                            size="small"
                                            color={valid ? 'default' : 'error'}
                                            sx={{fontFamily: 'monospace', fontSize: '0.8rem'}}
                                        />
                                    );
                                })
                            }
                        />

                        <Autocomplete
                            multiple
                            freeSolo
                            options={[]}
                            value={createPostLogoutUris}
                            onChange={(_, value) => setCreatePostLogoutUris(value as string[])}
                            inputValue={postLogoutUriInput}
                            onInputChange={(_, value, reason) => {
                                if (reason !== 'reset') setPostLogoutUriInput(value);
                            }}
                            onBlur={() => {
                                const merged = mergePending(createPostLogoutUris, postLogoutUriInput);
                                if (merged !== createPostLogoutUris) {
                                    setCreatePostLogoutUris(merged);
                                    setPostLogoutUriInput('');
                                }
                            }}
                            disabled={creating}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('oidcClients.create.postLogoutUrisLabel')}
                                    placeholder="https://app.example.com/logout"
                                    helperText={
                                        postLogoutUrisInvalid
                                            ? t('oidcClients.create.redirectUrisInvalid')
                                            : t('oidcClients.create.postLogoutUrisHelper')
                                    }
                                    error={postLogoutUrisInvalid}
                                />
                            )}
                            renderValue={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const valid = isValidUri(option);
                                    return (
                                        <Chip
                                            {...getTagProps({index})}
                                            key={option}
                                            label={option}
                                            size="small"
                                            color={valid ? 'default' : 'error'}
                                            sx={{fontFamily: 'monospace', fontSize: '0.8rem'}}
                                        />
                                    );
                                })
                            }
                        />

                        <Autocomplete
                            multiple
                            freeSolo
                            options={DEFAULT_SCOPES}
                            value={createScopes}
                            onChange={(_, value) => setCreateScopes(value as string[])}
                            inputValue={scopeInput}
                            onInputChange={(_, value, reason) => {
                                if (reason !== 'reset') setScopeInput(value);
                            }}
                            onBlur={() => {
                                const merged = mergePending(createScopes, scopeInput);
                                if (merged !== createScopes) {
                                    setCreateScopes(merged);
                                    setScopeInput('');
                                }
                            }}
                            disabled={creating}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('oidcClients.create.scopesLabel')}
                                    placeholder="support_api"
                                    helperText={t('oidcClients.create.scopesHelper')}
                                />
                            )}
                            renderValue={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({index})}
                                        key={option}
                                        label={option}
                                        size="small"
                                        sx={{fontFamily: 'monospace', fontSize: '0.8rem'}}
                                    />
                                ))
                            }
                        />

                        <Stack spacing={0.5}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={createAllowClientCredentials}
                                        onChange={(_, checked) => setCreateAllowClientCredentials(checked)}
                                        disabled={creating || createClientType === 'public'}
                                    />
                                }
                                label={t('oidcClients.create.allowClientCredentialsLabel')}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ml: 5.5, mt: -0.5}}>
                                {createClientType === 'public'
                                    ? t('oidcClients.create.allowClientCredentialsPublicHelper')
                                    : t('oidcClients.create.allowClientCredentialsHelper')}
                            </Typography>

                            <FormControlLabel
                                sx={{mt: 1}}
                                control={
                                    <Switch
                                        checked={createIsTrusted}
                                        onChange={(_, checked) => setCreateIsTrusted(checked)}
                                        disabled={creating}
                                    />
                                }
                                label={t('oidcClients.create.isTrustedLabel')}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ml: 5.5, mt: -0.5}}>
                                {t('oidcClients.create.isTrustedHelper')}
                            </Typography>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={creating}>
                        {t('oidcClients.create.cancel')}
                    </Button>
                    <Button
                        onClick={submitCreate}
                        variant="contained"
                        disabled={creating || !canSubmit}
                        startIcon={creating ? <CircularProgress size={16}/> : null}
                    >
                        {t('oidcClients.create.submit')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!revealed} onClose={() => setRevealed(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('oidcClients.reveal.title')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <Alert severity={revealed?.clientSecret ? 'warning' : 'info'}>
                            {revealed?.clientSecret
                                ? t('oidcClients.reveal.warning')
                                : t('oidcClients.reveal.publicInfo')}
                        </Alert>

                        <TextField
                            label={t('oidcClients.reveal.clientIdLabel')}
                            value={revealed?.clientId ?? ''}
                            fullWidth
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    sx: {fontFamily: 'monospace'},
                                    endAdornment: (
                                        <Tooltip title={t('oidcClients.reveal.copy')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => revealed?.clientId && copyToClipboard(revealed.clientId)}
                                            >
                                                <ContentCopy fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    ),
                                },
                            }}
                        />

                        {revealed?.clientSecret && (
                            <TextField
                                label={t('oidcClients.reveal.clientSecretLabel')}
                                value={revealed.clientSecret}
                                type={secretVisible ? 'text' : 'password'}
                                fullWidth
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        sx: {fontFamily: 'monospace'},
                                        endAdornment: (
                                            <Stack direction="row">
                                                <Tooltip title={secretVisible ? t('oidcClients.reveal.hide') : t('oidcClients.reveal.show')}>
                                                    <IconButton size="small" onClick={() => setSecretVisible((v) => !v)}>
                                                        {secretVisible
                                                            ? <VisibilityOff fontSize="small"/>
                                                            : <Visibility fontSize="small"/>}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('oidcClients.reveal.copy')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => revealed.clientSecret && copyToClipboard(revealed.clientSecret)}
                                                    >
                                                        <ContentCopy fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        ),
                                    },
                                }}
                            />
                        )}

                        <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 1}}>
                            <Chip
                                size="small"
                                label={t(`oidcClients.clientType.${revealed?.clientType ?? 'confidential'}`)}
                                color={revealed?.clientType === 'public' ? 'default' : 'primary'}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={t(`oidcClients.consentType.${revealed?.consentType ?? 'explicit'}`)}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevealed(null)} variant="contained">
                        {t('oidcClients.reveal.done')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OidcClients;
